import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getClientById,
  getClientSummary,
  getClients,
  getClientsSummary,
} from "../api";
import { formatRut } from "../utils/rut";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import ClientPaymentModal from "./ClientPaymentModal";
import ClientCreditModal from "./ClientCreditModal";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "./InlineLoading";

const formatMoney = (value) => formatPesoAmount(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-CL");
};

const ClientReceivables = ({ branchScopeId }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [summaryMap, setSummaryMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyWithBalance, setOnlyWithBalance] = useState(true);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const { user } = useCurrentUser();
  const canReadClients = hasPermission(user, "client.read");
  const canUpdateClient = hasPermission(user, "client.update");
  const canManageCredit = canUpdateClient && hasPermission(user, "branch.access.any");
  const canSelectBranch = hasPermission(user, "branch.access.any");
  const pageSize = 20;

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      if (!canReadClients) {
        setError("No tienes permisos para ver clientes.");
        setLoading(false);
        return;
      }
      const trimmedSearch = searchTerm.trim();
      const summaryParams = {
        page,
        pageSize,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      };
      if (canSelectBranch && branchScopeId) {
        summaryParams.branchId = branchScopeId;
      }
      const [clientsData, summariesData] = await Promise.all([
        getClients({
          page,
          pageSize,
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
        }),
        getClientsSummary(summaryParams),
      ]);
      const normalizedClients = normalizePaginatedResponse(clientsData);
      const normalizedSummaries = normalizePaginatedResponse(summariesData);
      setClients(normalizedClients.data || []);
      setTotalClients(normalizedClients.total || 0);
      setTotalPages(
        normalizedClients.totalPages || Math.max(1, Math.ceil((normalizedClients.total || 0) / pageSize))
      );
      const summaryEntries = (normalizedSummaries.data || []).reduce((acc, summary) => {
        if (summary?.clientId) {
          acc[summary.clientId] = summary;
        }
        return acc;
      }, {});
      setSummaryMap(summaryEntries);
    } catch (err) {
      console.error(err);
      setError("Error al cargar la cobranza.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [canReadClients, page, searchTerm, branchScopeId, canSelectBranch]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, onlyWithBalance, onlyOverdue]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const refreshClient = async (clientId) => {
    try {
      const summaryParams = {};
      if (canSelectBranch && branchScopeId) {
        summaryParams.branchId = branchScopeId;
      }
      const [clientData, summaryData] = await Promise.all([
        getClientById(clientId),
        getClientSummary(clientId, summaryParams),
      ]);
      setClients((prev) =>
        prev.map((client) => (client.id === clientId ? { ...client, ...clientData } : client))
      );
      setSummaryMap((prev) => ({ ...prev, [clientId]: summaryData }));
    } catch (err) {
      console.error(err);
    }
  };

  const rows = useMemo(() => {
    return clients
      .map((client) => ({ client, summary: summaryMap[client.id] }))
      .filter(({ client, summary }) => {
        if (!client) return false;
        const balance = Number(summary?.balance || 0);
        const overdue = Number(summary?.overdue || 0);
        if (onlyWithBalance && balance <= 0) return false;
        if (onlyOverdue && overdue <= 0) return false;
        return true;
      })
      .sort((a, b) => Number(b.summary?.balance || 0) - Number(a.summary?.balance || 0));
  }, [clients, summaryMap, onlyWithBalance, onlyOverdue]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.balance += Number(row.summary?.balance || 0);
        acc.overdue += Number(row.summary?.overdue || 0);
        acc.credit += Number(row.summary?.credit || 0);
        return acc;
      },
      { balance: 0, overdue: 0, credit: 0 }
    );
  }, [rows]);

  const openPaymentModal = (client, summary) => {
    if (!canUpdateClient) return;
    setSelectedClient(client);
    setSelectedSummary(summary);
    setShowPaymentModal(true);
  };

  const openCreditModal = (client) => {
    if (!canUpdateClient) return;
    setSelectedClient(client);
    setShowCreditModal(true);
  };

  const closeModals = () => {
    setSelectedClient(null);
    setSelectedSummary(null);
    setShowPaymentModal(false);
    setShowCreditModal(false);
  };

  if (loading) {
    return (
      <div className="p-6">
        <InlineLoading />
      </div>
    );
  }
  if (!canReadClients) {
    return (
      <div className="p-6 text-sm text-gray-600">
        No tienes permisos para ver clientes.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h1 className="text-2xl font-bold">Cobranza clientes</h1>
        {canReadClients && (
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Actualizar
          </button>
        )}
      </div>

      {error && <p className="text-red-600">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Saldo pendiente</p>
          <p className="text-xl font-semibold">${formatMoney(totals.balance)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Vencido</p>
          <p className="text-xl font-semibold text-red-600">${formatMoney(totals.overdue)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Credito a favor</p>
          <p className="text-xl font-semibold text-green-600">${formatMoney(totals.credit)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg shadow">
        <input
          type="text"
          placeholder="Buscar por nombre, rut o email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 rounded p-2 w-full"
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyWithBalance}
            onChange={(e) => setOnlyWithBalance(e.target.checked)}
          />
          Solo con saldo
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyOverdue}
            onChange={(e) => setOnlyOverdue(e.target.checked)}
          />
          Solo vencidos
        </label>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="p-3">Cliente</th>
              <th className="p-3">Saldo</th>
              <th className="p-3">Vencido</th>
              <th className="p-3">Credito</th>
              <th className="p-3">Limite</th>
              <th className="p-3">Ultimo pago</th>
              <th className="p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="p-4 text-center text-gray-500" colSpan={7}>
                  No hay clientes para mostrar.
                </td>
              </tr>
            ) : (
              rows.map(({ client, summary }) => {
                const balance = Number(summary?.balance || 0);
                const overdue = Number(summary?.overdue || 0);
                const credit = Number(summary?.credit || 0);
                const creditLimit = Number(client.creditLimit || 0);
                const hasLimit = Number.isFinite(creditLimit) && creditLimit > 0;
                const usagePercent = hasLimit ? Math.round((balance / creditLimit) * 100) : null;
                const overLimit = hasLimit && balance > creditLimit;

                return (
                  <tr key={client.id} className="border-b last:border-b-0">
                    <td className="p-3">
                      <div className="font-semibold">{client.name}</div>
                      <div className="text-xs text-gray-500">
                        {client.rut ? formatRut(client.rut) : "-"}
                      </div>
                      {client.creditBlocked && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded">
                          Bloqueado
                        </span>
                      )}
                      {client.creditLimitGraceUsedAt && (
                        <span className="inline-block mt-1 ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                          Gracia usada
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-semibold">${formatMoney(balance)}</td>
                    <td className="p-3 text-red-600">${formatMoney(overdue)}</td>
                    <td className="p-3 text-green-600">${formatMoney(credit)}</td>
                    <td className="p-3">
                      {hasLimit ? (
                        <div>
                          <div className={overLimit ? "text-red-600" : "text-gray-700"}>
                            ${formatMoney(balance)} / ${formatMoney(creditLimit)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {usagePercent}% usado
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">Sin limite</span>
                      )}
                    </td>
                    <td className="p-3">{formatDateTime(summary?.lastPaymentAt)}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                        {canUpdateClient && (
                          <button
                            onClick={() => openPaymentModal(client, summary)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            Pago
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/client/${client.id}`)}
                          className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                        >
                          Compras
                        </button>
                        {canUpdateClient && canManageCredit && (
                          <button
                            onClick={() => openCreditModal(client)}
                            className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                          >
                            Credito
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalClients > pageSize && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500">
          <div>
            Mostrando {totalClients === 0 ? 0 : (page - 1) * pageSize + 1}-
            {Math.min(page * pageSize, totalClients)} de {totalClients}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Anterior
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page >= totalPages}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {showPaymentModal && selectedClient && (
        <ClientPaymentModal
          client={selectedClient}
          summary={selectedSummary}
          onClose={closeModals}
          onSaved={() => refreshClient(selectedClient.id)}
          canUpdateClient={canUpdateClient}
        />
      )}

      {showCreditModal && selectedClient && (
        <ClientCreditModal
          client={selectedClient}
          canManageCredit={canManageCredit}
          onClose={closeModals}
          onSaved={(updates) => {
            if (updates) {
              setClients((prev) =>
                prev.map((client) =>
                  client.id === selectedClient.id ? { ...client, ...updates } : client
                )
              );
            }
            return refreshClient(selectedClient.id);
          }}
          canUpdateClient={canUpdateClient}
        />
      )}
    </div>
  );
};

export default ClientReceivables;
